(() => {
  'use strict';

  function variantForModel(variants, modelIndex, model, color) {
    const matches = variants.filter((variant) => variant.options[modelIndex] === model && variant.available);
    return matches.find((variant) => variant.options[1 - modelIndex] === color) || matches[0];
  }

  function mediaForVariant(media, variant) {
    const matching = media.filter((item) => item.alt?.trim().toLowerCase() === variant.title.trim().toLowerCase());
    return matching.length ? matching : media.filter((item) => !item.alt);
  }

  function choicesForCase(variants, select) {
    if (!select) return variants;
    const options = new Map(Array.from(select.options, (option) => [Number(option.value), option]));
    return variants.filter((variant) => options.has(variant.id)).map((variant) => ({
      ...variant,
      available: variant.available && !options.get(variant.id).disabled && options.get(variant.id).dataset.bundleAvailable !== 'false'
    }));
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { variantForModel, mediaForVariant, choicesForCase };
  if (typeof customElements === 'undefined' || customElements.get('xtrem-bf-product')) return;

  const colorMap = { 'nova white': '#fff', 'noir black': '#000', berry: '#ff466c', lime: '#d0ed38', 'mist blue': '#acd4e8', 'cosmic orange': '#ff8900' };
  const tierSelector = '.recommended-offer-widget__qb-tier';
  const selectSelector = 'select[data-bundle-value-mode="variant-id"]';
  const escape = (text) => String(text).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  class XtremBfProduct extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-product-config]').textContent);
      this.variants = this.config.variants;
      this.variant = this.variants.find((variant) => variant.id === this.config.selectedId);
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.dialog = this.querySelector('dialog');
      this.scrollPanel = this.querySelector('.xbf-purchase-scroll');
      this.header = document.querySelector('.xbf-header');
      this.footer = document.querySelector('.xbf-footer');
      this.mobile = matchMedia('(max-width: 989px)');
      this.formatMoney = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.caseIndex = 0;
      this.modelConfirmed = new URL(location.href).searchParams.has('variant');
      this.galleryIndex = 0;
      this.quantity = 1;
      this.submitObserver = new MutationObserver(() => this.syncSubmitState());
      this.submitObserver.observe(this.form.querySelector('[type="submit"]'), { attributes: true });
      this.submitObserver.observe(this.querySelector('.product-form__error-message-wrapper'), { childList: true, subtree: true, characterData: true, attributes: true });
      // Bun owns the request/discount payload but suppresses its failure banner.
      // Observe cart-add responses on this alternate page; never rewrite the request or response.
      this.originalFetch = window.fetch;
      this.cartFetch = (input, options) => {
        const url = new URL(input instanceof Request ? input.url : String(input), location.href);
        const method = options?.method || (input instanceof Request ? input.method : 'GET');
        if (!/\/cart\/add(?:\.js|\.json)?$/.test(url.pathname) || method.toUpperCase() !== 'POST') {
          return this.originalFetch.call(window, input, options);
        }
        const productForm = this.querySelector('product-form');
        const submit = this.form.querySelector('[type="submit"]');
        const reportError = (message) => productForm.handleErrorMessage(message);
        productForm.handleErrorMessage();
        this.cartPending = true;
        submit.disabled = true;
        this.syncSubmitState();
        return this.originalFetch.call(window, input, options).then(async (response) => {
          if (!response.ok) {
            const error = await response.clone().json().catch(() => ({}));
            reportError(error.description || error.message || 'Unable to add to cart. Please try again.');
          }
          return response;
        }, (error) => {
          reportError('Unable to connect. Please check your connection and try again.');
          throw error;
        }).finally(() => {
          this.cartPending = false;
          submit.disabled = !this.variant.available || this.activeTier()?.getAttribute('aria-disabled') === 'true';
          this.syncSubmitState();
        });
      };
      window.fetch = this.cartFetch;
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.querySelector('.xbf-gallery-main').addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.showMedia(this.galleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, { signal });
      let touchStart;
      const gallery = this.querySelector('.xbf-gallery-main');
      gallery.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0]; }, { passive: true, signal });
      gallery.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        if (Math.abs(touch.clientX - touchStart.clientX) > 40 && Math.abs(touch.clientY - touchStart.clientY) < 60) {
          this.showMedia(this.galleryIndex + (touch.clientX < touchStart.clientX ? 1 : -1));
        }
      }, { passive: true, signal });
      this.scrollPanel.addEventListener('scroll', () => this.updateSticky(), { passive: true, signal });
      window.addEventListener('scroll', () => this.updateSticky(), { passive: true, signal });
      this.mobile.addEventListener('change', () => this.updateSticky(), { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientY < rect.top || event.clientX < rect.left || event.clientX > rect.right) this.dialog.close();
        }
      }, { signal });
      this.dialog.addEventListener('close', () => {
        const opener = this.dialogOpener.isConnected ? this.dialogOpener : this.querySelector(`[data-model-trigger="${this.caseIndex}"]`);
        opener.focus();
      }, { signal });
      this.appFinder = new MutationObserver(() => this.connectOffer());
      this.appFinder.observe(document.body, { childList: true, subtree: true });
      this.updateVariant(this.variant, false);
      this.renderOffers();
      this.connectOffer();
      this.updateSticky();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.appFinder.disconnect();
      this.submitObserver.disconnect();
      if (window.fetch === this.cartFetch) window.fetch = this.originalFetch;
      this.appObserver?.disconnect();
      cancelAnimationFrame(this.appFrame);
    }

    money(cents) { return this.formatMoney.format(cents / 100); }

    connectOffer() {
      const widget = document.querySelector('#recommended-offer-widget[data-offer-kind="qb"]');
      const payload = window.__bundleWidgetOfferCache?.[`${Shopify.shop}::gid://shopify/Product/${this.config.id}`]?.payload;
      if (!widget || !payload?.offer?.tiers || widget.querySelectorAll(tierSelector).length !== payload.offer.tiers.length) return;
      this.widget = widget;
      this.offer = payload.offer;
      this.nativeTiers = Array.from(widget.querySelectorAll(tierSelector));
      widget.classList.add('xbf-app-proxy');
      widget.setAttribute('aria-hidden', 'true');
      widget.inert = true;
      this.appFinder.disconnect();
      this.appObserver = new MutationObserver(() => {
        cancelAnimationFrame(this.appFrame);
        this.appFrame = requestAnimationFrame(() => this.syncOffer());
      });
      this.appObserver.observe(widget, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-pressed', 'aria-disabled', 'disabled', 'data-bundle-available'] });
      this.nativeTiers[0].click();
      const first = this.nativeTiers[0].querySelector(selectSelector);
      if (first && Array.from(first.options).some((option) => Number(option.value) === this.variant.id)) {
        this.updateVariant(this.variant);
      }
      this.syncOffer();
    }

    activeTier() { return this.nativeTiers?.find((tier) => tier.getAttribute('aria-pressed') === 'true'); }

    activeSelects() { return this.activeTier() ? Array.from(this.activeTier().querySelectorAll(selectSelector)) : []; }

    syncOffer() {
      this.nativeTiers = Array.from(this.widget.querySelectorAll(tierSelector));
      const index = this.nativeTiers.indexOf(this.activeTier());
      if (index < 0) return;
      this.quantity = this.offer.tiers[index].quantity;
      const first = this.activeSelects()[0];
      if (first) {
        const selected = this.variants.find((variant) => variant.id === Number(first.value));
        if (selected && selected.id !== this.variant.id) this.updateVariant(selected, false);
      }
      const choices = choicesForCase(this.variants, first);
      const colorMarkup = this.colorButtons(this.variant, choices, 0);
      if (colorMarkup !== this.colorMarkup) {
        this.colorMarkup = colorMarkup;
        this.querySelector('[data-colors]').innerHTML = colorMarkup;
      }
      if (!this.cartPending) this.form.querySelector('[type="submit"]').disabled = !this.variant.available || this.activeTier().getAttribute('aria-disabled') === 'true';
      this.renderOffers();
      this.renderCaseOptions();
      this.syncSubmitState();
      if (this.dialog.open) this.renderDialog();
    }

    renderOffers() {
      const tiers = this.offer?.tiers || [{ quantity: 1, discount: 0, title: '1 case' }];
      const selected = this.nativeTiers ? this.nativeTiers.indexOf(this.activeTier()) : 0;
      const data = tiers.map((tier, index) => ({
        title: tier.discountType === 'percentage' && tier.discount > 0 ? `Save ${tier.discount}%` : (tier.subtitle || (index === 0 ? 'Standard price' : tier.title)),
        quantity: tier.quantity,
        price: this.nativeTiers?.[index]?.querySelector('.recommended-offer-widget__tier-price')?.textContent.trim() || this.money(this.variant.price),
        comparePrice: this.nativeTiers?.[index]?.querySelector('.recommended-offer-widget__tier-price + div')?.textContent.trim() || (this.variant.compareAt > this.variant.price ? this.money(this.variant.compareAt) : ''),
        disabled: this.nativeTiers?.[index]?.getAttribute('aria-disabled') === 'true'
      }));
      const signature = JSON.stringify([selected, data]);
      if (signature !== this.offerSignature) {
        const focusedTier = document.activeElement?.dataset.tier;
        this.offerSignature = signature;
        this.querySelector('[data-tiers]').innerHTML = data.map((tier, index) => `<button class="xbf-tier" type="button" data-tier="${index}" aria-pressed="${index === selected}" ${tier.disabled ? 'disabled' : ''}><strong>${escape(tier.title)}</strong><small>${tier.quantity} ${tier.quantity === 1 ? 'case' : 'cases'} &nbsp; ${escape(tier.price)}</small></button>`).join('');
        if (focusedTier !== undefined) this.querySelector(`[data-tier="${focusedTier}"]`).focus({ preventScroll: true });
      }
      const active = data[selected] || data[0];
      this.querySelector('[data-tier-progress]').style.width = `${((selected + 1) / tiers.length) * 100}%`;
      this.querySelectorAll('[data-cta-badge]').forEach((node) => { node.textContent = active.title; });
      this.querySelectorAll('[data-count]').forEach((node) => { node.textContent = this.quantity; });
      this.querySelectorAll('[data-sticky-price]').forEach((node) => { node.textContent = active.price; });
      const compare = this.querySelector('[data-sticky-compare]');
      compare.textContent = active.comparePrice;
      compare.hidden = !active.comparePrice || active.comparePrice === active.price;
      this.querySelector('[data-sticky-sale]').hidden = compare.hidden;
    }

    colorButtons(variant, choices, index) {
      const model = variant.options[this.config.modelIndex];
      return choices.filter((choice) => choice.options[this.config.modelIndex] === model).map((choice) => {
        const color = choice.options[this.config.colorIndex];
        return `<button type="button" class="xbf-color" data-color-variant="${choice.id}" data-case="${index}" aria-label="${escape(color)}${choice.available ? '' : ' — sold out'}" title="${escape(color)}" aria-pressed="${choice.id === variant.id}" ${choice.available ? '' : 'disabled'}><i style="--swatch:${colorMap[color.toLowerCase()] || '#ddd'}"></i></button>`;
      }).join('');
    }

    renderCaseOptions() {
      const selects = this.activeSelects();
      const signature = JSON.stringify(selects.map((select) => [select.value, choicesForCase(this.variants, select).map(({ id, available }) => [id, available])]));
      if (signature === this.casesSignature) return;
      this.casesSignature = signature;
      this.querySelector('[data-case-options]').innerHTML = selects.slice(1).map((select, index) => {
        const variant = this.variants.find((item) => item.id === Number(select.value));
        return `<div class="xbf-case-option"><label>Case #${index + 2}</label><div class="xbf-option-row"><span>Model</span><button type="button" class="xbf-model-trigger" data-model-trigger="${index + 1}"><span>${escape(variant.options[this.config.modelIndex])}</span><span aria-hidden="true">+</span></button></div><div class="xbf-colors" role="group" aria-label="Case ${index + 2} color">${this.colorButtons(variant, choicesForCase(this.variants, select), index + 1)}</div><p class="xbf-case-summary">${escape(variant.title)}</p></div>`;
      }).join('');
    }

    updateVariant(variant, updateNative = true) {
      this.variant = variant;
      const input = this.form.querySelector('[name="id"]');
      input.value = variant.id;
      this.querySelector('[data-model-name]').textContent = variant.options[this.config.modelIndex];
      this.querySelector('[data-color-name]').textContent = `: ${variant.options[this.config.colorIndex]}`;
      this.colorMarkup = this.colorButtons(variant, choicesForCase(this.variants, this.activeSelects()[0]), 0);
      this.querySelector('[data-colors]').innerHTML = this.colorMarkup;
      this.querySelector('[data-price]').textContent = `${this.money(variant.price)} ${this.config.currency}`;
      const compare = this.querySelector('[data-compare]');
      compare.textContent = this.money(variant.compareAt);
      compare.hidden = variant.compareAt <= variant.price;
      this.querySelector('[data-sale-label]').hidden = compare.hidden;
      ['model', 'color'].forEach((key) => {
        const value = variant.options[this.config[`${key}Index`]];
        this.querySelectorAll(`[data-sticky-${key}]`).forEach((node) => { node.textContent = value; });
        this.querySelector(`[data-detail-${key}]`).textContent = value;
      });
      this.querySelectorAll('.xbf-add-button:not(.xbf-dialog-done)').forEach((button) => {
        button.disabled = !variant.available;
        button.querySelector('span').textContent = variant.available ? 'Add to cart' : 'Sold out';
      });
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      this.galleryMedia = mediaForVariant(this.config.media, variant);
      if (!this.galleryMedia.length) this.galleryMedia = this.config.media.slice(0, 1);
      this.querySelector('[data-thumbnails]').innerHTML = this.galleryMedia.map((media, index) => `<button type="button" class="xbf-thumb" data-gallery-index="${index}" aria-label="View image ${index + 1}" aria-pressed="false"><img src="${escape(media.thumb)}" width="112" height="112" alt="${escape(variant.title)}" loading="lazy"></button>`).join('');
      this.querySelector('[data-dots]').innerHTML = this.galleryMedia.map((_, index) => `<button type="button" class="xbf-dot" data-gallery-index="${index}" aria-label="View image ${index + 1}" aria-current="false"></button>`).join('');
      this.showMedia(0);
      this.querySelectorAll('[data-sticky-image]').forEach((node) => { node.src = this.galleryMedia[0].thumb; });
      if (updateNative) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const first = this.activeSelects()[0];
        if (first) { first.value = variant.id; first.dispatchEvent(new Event('change', { bubbles: true })); }
        // Inactive tiers preview this model/color. Preserve any other cases in the active tier.
        this.nativeTiers?.filter((tier) => tier !== this.activeTier()).forEach((tier) => {
          tier.querySelectorAll(selectSelector).forEach((select) => {
            if (Array.from(select.options).some((option) => Number(option.value) === variant.id)) {
              select.value = variant.id;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      }
      this.renderOffers();
    }

    showMedia(index) {
      this.galleryIndex = (index + this.galleryMedia.length) % this.galleryMedia.length;
      const media = this.galleryMedia[this.galleryIndex];
      const container = this.querySelector('[data-media]');
      if (media.type === 'video') {
        container.innerHTML = `<video controls playsinline preload="metadata" poster="${escape(media.src)}">${media.sources.map((source) => `<source src="${escape(source.url)}" type="${escape(source.mime_type)}">`).join('')}</video>`;
      } else if (media.type === 'external_video') {
        const src = media.host === 'youtube' ? `https://www.youtube.com/embed/${encodeURIComponent(media.externalId)}` : `https://player.vimeo.com/video/${encodeURIComponent(media.externalId)}`;
        container.innerHTML = `<iframe src="${src}" title="Product video" allow="fullscreen" allowfullscreen></iframe>`;
      } else {
        container.innerHTML = `<img class="xbf-main-image" src="${escape(media.src)}" alt="${escape(this.variant.title)} — image ${this.galleryIndex + 1}" width="700" height="700" fetchpriority="high">`;
      }
      this.querySelectorAll('[data-gallery-index]').forEach((button) => button.setAttribute(button.classList.contains('xbf-dot') ? 'aria-current' : 'aria-pressed', String(Number(button.dataset.galleryIndex) === this.galleryIndex)));
      this.querySelector('[data-gallery-progress]').style.width = `${((this.galleryIndex + 1) / this.galleryMedia.length) * 100}%`;
      const thumb = this.querySelector(`.xbf-thumb[data-gallery-index="${this.galleryIndex}"]`);
      const strip = this.querySelector('[data-thumbnails]');
      if (this.mobile.matches) strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - 10 });
      else strip.scrollTo({ top: thumb.offsetTop - strip.offsetTop });
    }

    renderDialog() {
      const focused = this.dialog.contains(document.activeElement) ? { ...document.activeElement.dataset } : {};
      const select = this.activeSelects()[this.caseIndex];
      const variant = select ? this.variants.find((item) => item.id === Number(select.value)) : this.variant;
      const choices = choicesForCase(this.variants, select);
      const models = [...new Set(choices.map((item) => item.options[this.config.modelIndex]))];
      this.querySelector('[data-model-grid]').innerHTML = models.map((model) => `<button type="button" data-model="${escape(model)}" aria-pressed="${model === variant.options[this.config.modelIndex]}" ${choices.some((item) => item.options[this.config.modelIndex] === model && item.available) ? '' : 'disabled'}>${escape(model)}</button>`).join('');
      this.querySelector('[data-dialog-colors]').innerHTML = this.colorButtons(variant, choices, this.caseIndex);
      this.querySelector('[data-dialog-title]').textContent = variant.title;
      this.querySelector('[data-dialog-price]').textContent = this.money(variant.price);
      this.querySelector('[data-dialog-image]').src = (mediaForVariant(this.config.media, variant)[0] || this.config.media[0]).thumb;
      if (focused.model) this.querySelector(`[data-model="${CSS.escape(focused.model)}"]`).focus({ preventScroll: true });
      else if (focused.colorVariant) this.querySelector(`[data-dialog-colors] [data-color-variant="${focused.colorVariant}"]`).focus({ preventScroll: true });
    }

    openModels(index, opener) {
      this.caseIndex = index;
      this.dialogOpener = opener;
      this.renderDialog();
      this.dialog.showModal();
    }

    chooseVariant(id, index) {
      const variant = this.variants.find((item) => item.id === id);
      this.modelConfirmed = true;
      if (index === 0) this.updateVariant(variant);
      else {
        const select = this.activeSelects()[index];
        select.value = variant.id;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.renderCaseOptions();
      if (this.dialog.open) this.renderDialog();
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (button.hasAttribute('data-open-reviews')) document.querySelector('.jdgm-revs-tab-btn')?.click();
      if (button.hasAttribute('data-gallery-step')) this.showMedia(this.galleryIndex + Number(button.dataset.galleryStep));
      if (button.hasAttribute('data-gallery-index')) this.showMedia(Number(button.dataset.galleryIndex));
      if (button.hasAttribute('data-model-trigger')) this.openModels(Number(button.dataset.modelTrigger), button);
      if (button.hasAttribute('data-color-variant')) {
        this.chooseVariant(Number(button.dataset.colorVariant), Number(button.dataset.case));
        const scope = this.dialog.open ? this.dialog : this;
        scope.querySelector(`[data-color-variant="${button.dataset.colorVariant}"][data-case="${button.dataset.case}"]`).focus({ preventScroll: true });
      }
      if (button.hasAttribute('data-model')) {
        const select = this.activeSelects()[this.caseIndex];
        const current = select ? this.variants.find((item) => item.id === Number(select.value)) : this.variant;
        const variant = variantForModel(choicesForCase(this.variants, select), this.config.modelIndex, button.dataset.model, current.options[this.config.colorIndex]);
        this.chooseVariant(variant.id, this.caseIndex);
        this.querySelector(`[data-model="${CSS.escape(button.dataset.model)}"]`).focus();
      }
      if (button.hasAttribute('data-close-dialog')) { this.modelConfirmed = true; this.dialog.close(); }
      if (button.hasAttribute('data-tier') && this.nativeTiers) {
        const index = Number(button.dataset.tier);
        this.nativeTiers[index].click();
        this.activeSelects().forEach((select) => {
          if (Array.from(select.options).some((option) => Number(option.value) === this.variant.id)) {
            select.value = this.variant.id;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        this.syncOffer();
        this.querySelector(`[data-tier="${index}"]`).focus();
      }
      if (button.hasAttribute('data-sticky-submit')) {
        if (this.mobile.matches && !this.modelConfirmed) this.openModels(0, button);
        else this.form.querySelector('[type="submit"]').click();
      }
    }

    updateSticky() {
      const belowButton = this.querySelector('.xbf-main-cta').getBoundingClientRect().bottom < 80;
      const mobileVisible = this.mobile.matches && this.header.getBoundingClientRect().bottom <= 0;
      const desktopVisible = !this.mobile.matches && belowButton && this.footer.getBoundingClientRect().top >= window.innerHeight;
      const sticky = this.querySelector('[data-sticky-buy]');
      sticky.classList.toggle('is-visible', this.mobile.matches || desktopVisible);
      sticky.inert = !this.mobile.matches && !desktopVisible;
      const mobileSticky = this.querySelector('[data-mobile-sticky]');
      mobileSticky.classList.toggle('is-visible', mobileVisible);
      mobileSticky.inert = !mobileVisible;
    }

    syncSubmitState() {
      const source = this.form.querySelector('[type="submit"]');
      const busy = this.cartPending || source.getAttribute('aria-disabled') === 'true' || source.classList.contains('loading');
      this.querySelectorAll('[data-sticky-submit]').forEach((button) => {
        button.disabled = source.disabled || busy;
        button.setAttribute('aria-busy', String(busy));
        button.querySelector('span').textContent = busy ? 'Adding…' : (this.variant.available ? 'Add to cart' : 'Sold out');
      });
      const error = this.querySelector('.product-form__error-message-wrapper');
      const message = !error.hidden ? error.textContent.trim() : '';
      this.querySelectorAll('[data-sticky-error]').forEach((stickyError) => {
        stickyError.textContent = message;
        stickyError.hidden = !message;
      });
    }
  }
  customElements.define('xtrem-bf-product', XtremBfProduct);
})();
